import {
  useSyncCheckAiSearchTalentHuntJobQuery,
  useTriggerAiProcessingTalentHuntJobMutation,
} from '@/app/features/recruitment/job-applicants/job-applicants.api';
import type { CustomModalProps } from '@/components/CustomModal';
import { AiSearchIcon, CheckCircleIcon, ReloadIcon } from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { Button, Flex, Modal, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

interface Props extends Omit<CustomModalProps, 'id'> {
  id?: string;
}

export default function AiSearchModal({ open, handleClose, id }: Props) {
  const navigate=useNavigate();
  // Poll sync status every 5s while modal is open and id is present
  const [pollingInterval, setPollingInterval] = useState(0);

  const { data: syncData, isLoading: isSyncLoading } =
    useSyncCheckAiSearchTalentHuntJobQuery(id as string, {
      skip: !open || !id,
      pollingInterval,
    });

  useEffect(() => {
    if ((syncData as any)?.data?.status === 'processing') {
      setPollingInterval(3000);
    } else {
      setPollingInterval(0);
    }
  }, [syncData]);

  const [triggerAiProcessing, { isLoading }] =
    useTriggerAiProcessingTalentHuntJobMutation();

  const status = (syncData as any)?.data?.status as string | undefined;
  const isProcessing = status === 'processing';
  const isCompleted = status === 'completed';

  const handleStart = async () => {
    if (!id) return;
    try {
      await triggerAiProcessing(id).unwrap();
      toast('success', 'AI processing started successfully');
    } catch (error) {
      toast('error', getErrorMessage(error) || 'Failed to start AI processing');
    }
  };

  const renderContent = () => {
    // Loading sync status
    if (isSyncLoading) {
      return (
        <Flex
          vertical
          align="center"
          justify="center"
          gap={16}
          className="py-10"
        >
          <Spin size="large" />
          <p className="text-white text-base">Checking AI search status…</p>
        </Flex>
      );
    }

    // Already processing
    if (isProcessing) {
      return (
        <div className="text-center py-8 space-y-5">
          <Flex justify="center">
            <div className="relative">
              <AiSearchIcon className="text-theme-primary" size={56} />
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping" />
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full" />
            </div>
          </Flex>
          <h3 className="text-white text-2xl font-bold">
            Your Request is Processing
          </h3>
          <p className="text-slate-400 text-base">
            The AI is currently hunting candidates for this position. This may
            take a few minutes. The page will update automatically.
          </p>
          <div className="flex items-center justify-center gap-2 pt-2">
            <span className="w-2 h-2 bg-theme-primary rounded-full animate-bounce [animation-delay:-0.3s]" />
            <span className="w-2 h-2 bg-theme-primary rounded-full animate-bounce [animation-delay:-0.15s]" />
            <span className="w-2 h-2 bg-theme-primary rounded-full animate-bounce" />
          </div>
        </div>
      );
    }

    // Completed
    if (isCompleted) {
      return (
        <div className="text-center py-8 space-y-5">
          <Flex justify="center">
            <CheckCircleIcon className="text-theme-success-500" size={56} />
          </Flex>
          <h3 className="text-white text-2xl font-bold">
            AI Search Completed!
          </h3>
          <p className="text-slate-400 text-base">
            The AI has finished hunting candidates for this position. Check the
            TalentHunt details to see the suggested candidates.
          </p>
          <Button
            block
            size="large"
            className="btn-primary mt-4"
            onClick={() => {handleClose();navigate(`talent-hunt/${id}`)}}
          >
            View Suggestions
          </Button>
        </div>
      );
    }

    // Default: not started yet
    return (
      <div className="text-center py-8 space-y-5">
        <Flex justify="center">
          <AiSearchIcon className="text-theme-primary" size={56} />
        </Flex>
        <h3 className="text-white text-2xl font-bold">
          AI Search will hunt you some suggestions for this position!
        </h3>
        <p className="text-slate-400 text-base">
          Click below to start the TalentHunt Feature and let AI suggest the
          best candidates automatically.
        </p>
        <Button
          block
          size="large"
          className="btn-primary mt-4"
          loading={isLoading}
          icon={<ReloadIcon size={18} />}
          onClick={handleStart}
        >
          Start Hunting Candidates
        </Button>
      </div>
    );
  };

  return (
    <Modal
      width={420}
      open={open}
      footer={null}
      closable={true}
      centered={true}
      className="custom-modal"
      rootClassName="custom-bg-modal"
      onCancel={() => handleClose()}
    >
      <div className="p-2">{renderContent()}</div>
    </Modal>
  );
}
